/**
 * Helpers that turn the live `/roles/modules` dependency graph into the
 * render-ready permission tree, enforce the feature dependency rules, and
 * assemble the `role_feature_access` body for create / update.
 */
import {
  ROLE_MODULE_CATALOG,
  getFeatureDescription,
  getFeatureName,
  getModuleName,
} from '../constants/roleModuleCatalog'
import type {
  RoleDetail,
  RoleFeatureAccessDto,
  RoleModuleNode,
} from '../services/roles/roles.types'
import type { RolePermissionModule } from '../types'

/** Feature-access selection: `featureId` → granted. */
export type FeatureSelection = Record<number, boolean>

/**
 * Merge the live `/roles/modules` graph (structure + dependency graph — the
 * source of truth) with display names from the interim catalog. Modules with
 * no real features (e.g. AUD's `{ feature_id: null }` placeholder) are dropped.
 */
export function buildPermissionTree(
  modules: RoleModuleNode[]
): RolePermissionModule[] {
  return modules
    .map((module) => {
      const features = module.features
        .filter((feature) => feature.feature_id != null)
        .map((feature) => {
          const featureId = feature.feature_id as number
          return {
            featureId,
            name: getFeatureName(module.module_id, featureId),
            description: getFeatureDescription(module.module_id, featureId),
            dependsOn: feature.feature_dependency ?? [],
          }
        })
        .sort((a, b) => a.featureId - b.featureId)

      return {
        moduleId: module.module_id,
        name: getModuleName(module.module_id),
        description:
          ROLE_MODULE_CATALOG[module.module_id]?.description ?? '',
        features,
      }
    })
    .filter((module) => module.features.length > 0)
    .sort((a, b) => a.moduleId - b.moduleId)
}

/**
 * Toggle one feature, enforcing the dependency graph:
 * - Enabling a feature enables everything it depends on (transitively).
 * - Disabling a feature disables every feature that depends on it (transitively).
 */
export function applyFeatureToggle(
  tree: RolePermissionModule[],
  current: FeatureSelection,
  featureId: number,
  nextValue: boolean
): FeatureSelection {
  const next: FeatureSelection = { ...current, [featureId]: nextValue }
  const allFeatures = tree.flatMap((module) => module.features)
  const byId = new Map(allFeatures.map((feature) => [feature.featureId, feature]))

  if (nextValue) {
    // Enable dependencies transitively.
    const stack = [...(byId.get(featureId)?.dependsOn ?? [])]
    while (stack.length) {
      const depId = stack.pop() as number
      if (next[depId]) continue
      next[depId] = true
      stack.push(...(byId.get(depId)?.dependsOn ?? []))
    }
  } else {
    // Disable dependents transitively.
    const stack = [featureId]
    while (stack.length) {
      const id = stack.pop() as number
      for (const feature of allFeatures) {
        if (feature.dependsOn.includes(id) && next[feature.featureId]) {
          next[feature.featureId] = false
          stack.push(feature.featureId)
        }
      }
    }
  }

  return next
}

/** Enable / disable every feature in a module, honouring dependency rules. */
export function applyModuleToggle(
  tree: RolePermissionModule[],
  current: FeatureSelection,
  moduleId: number,
  nextValue: boolean
): FeatureSelection {
  const module = tree.find((entry) => entry.moduleId === moduleId)
  if (!module) return current

  return module.features.reduce(
    (selection, feature) =>
      applyFeatureToggle(tree, selection, feature.featureId, nextValue),
    current
  )
}

/** Pre-fill the selection map for Edit mode from a role's detail response. */
export function buildSelectionFromDetail(detail: RoleDetail): FeatureSelection {
  const selection: FeatureSelection = {}
  for (const module of detail.role_feature_access) {
    for (const feature of module.features) {
      selection[feature.feature_id] = feature.has_access
    }
  }
  return selection
}

/**
 * Assemble the `role_feature_access` body — every feature in the tree with an
 * explicit `has_access` flag, so the backend gets the complete state on both
 * create and update.
 */
export function buildRoleFeatureAccess(
  tree: RolePermissionModule[],
  selection: FeatureSelection
): RoleFeatureAccessDto[] {
  return tree.flatMap((module) =>
    module.features.map((feature) => ({
      module_id: module.moduleId,
      feature_id: feature.featureId,
      has_access: selection[feature.featureId] ?? false,
    }))
  )
}

/** `true` when at least one feature is granted. */
export function hasAnyAccess(selection: FeatureSelection): boolean {
  return Object.values(selection).some(Boolean)
}
